import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@asm/ui";
import {
  Clock,
  Plus,
  Minus,
  RefreshCw,
  AlertTriangle,
  Globe,
  Server,
  Network,
  ArrowRight,
  Cloud,
} from "lucide-react";
import { riskApi, type TimelineEvent } from "@/lib/api";

// Cloud provider icons mapping
const cloudProviderIcons: Record<string, { icon: string; color: string; name: string }> = {
  "amazon": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "aws": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "microsoft": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "azure": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "google": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "gcp": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "digitalocean": { icon: "/icons/digitalocean.svg", color: "#0080FF", name: "DigitalOcean" },
  "cloudflare": { icon: "/icons/cloudflare.svg", color: "#F38020", name: "Cloudflare" },
  "linode": { icon: "/icons/linode.svg", color: "#00A95C", name: "Linode" },
  "akamai": { icon: "/icons/akamai.svg", color: "#0096D6", name: "Akamai" },
  "fastly": { icon: "/icons/fastly.svg", color: "#FF282D", name: "Fastly" },
  "ovh": { icon: "/icons/ovh.svg", color: "#123F6D", name: "OVH" },
  "hetzner": { icon: "/icons/hetzner.svg", color: "#D50C2D", name: "Hetzner" },
  "vultr": { icon: "/icons/vultr.svg", color: "#007BFC", name: "Vultr" },
  "oracle": { icon: "/icons/oracle.svg", color: "#F80000", name: "Oracle Cloud" },
  "ibm": { icon: "/icons/ibm.svg", color: "#1F70C1", name: "IBM Cloud" },
  "alibaba": { icon: "/icons/alibaba.svg", color: "#FF6A00", name: "Alibaba Cloud" },
};

function getCloudProviderInfo(cloudProvider?: string): { icon: string; color: string; name: string } | null {
  if (!cloudProvider) return null;
  const term = cloudProvider.toLowerCase();

  // Direct match
  if (cloudProviderIcons[term]) {
    return cloudProviderIcons[term];
  }
  // Partial match
  for (const [key, value] of Object.entries(cloudProviderIcons)) {
    if (term.includes(key) || key.includes(term)) {
      return value;
    }
  }
  return null;
}

function formatTimestamp(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return "Yesterday " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" }) + " " +
           date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
}

function groupEventsByDate(events: TimelineEvent[]): Map<string, TimelineEvent[]> {
  const groups = new Map<string, TimelineEvent[]>();

  events.forEach((event) => {
    const date = new Date(event.timestamp);
    const key = date.toLocaleDateString([], {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(event);
  });

  return groups;
}

/**
 * Parse port identifier into IP and port/protocol
 * Input: "192.168.1.1:443/tcp" -> { ip: "192.168.1.1", port: "443/tcp" }
 */
function parsePortIdentifier(identifier: string): { ip: string; port: string } | null {
  // Port format: IP:port/protocol (e.g., "192.168.1.1:443/tcp")
  const match = identifier.match(/^(.+):(\d+\/\w+)$/);
  if (match) {
    return { ip: match[1], port: match[2] };
  }
  return null;
}

export default function AssetTimeline() {
  const [days, setDays] = useState<string>("7");
  const [eventFilter, setEventFilter] = useState<string>("all");

  const {
    data: timeline,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ["asset-timeline", days],
    queryFn: () => riskApi.getTimeline({ days: parseInt(days), limit: 200 }),
    staleTime: 30_000,
  });

  const filteredEvents = timeline?.events.filter((event) => {
    if (eventFilter === "all") return true;
    return event.event_type === eventFilter;
  }) || [];

  const groupedEvents = groupEventsByDate(filteredEvents);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Clock className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">Unable to load timeline</h3>
        <p className="text-muted-foreground mt-1">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>

              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Event type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                  <SelectItem value="removed">Removed</SelectItem>
                  <SelectItem value="status_changed">Status Changed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Asset Timeline
          </CardTitle>
          <CardDescription>
            {timeline?.total || 0} events in the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
              <Clock className="h-12 w-12 opacity-50 mb-4" />
              <p>No events in the selected period</p>
              <p className="text-sm">Asset changes will appear here as they occur</p>
            </div>
          ) : (
            <div className="space-y-8">
              {Array.from(groupedEvents.entries()).map(([date, events]) => (
                <div key={date}>
                  {/* Date header */}
                  <div className="sticky top-0 bg-background/95 backdrop-blur py-2 mb-4 border-b">
                    <h3 className="text-sm font-semibold text-muted-foreground">
                      {date}
                    </h3>
                  </div>

                  {/* Events for this date */}
                  <div className="space-y-3 relative">
                    {/* Vertical timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

                    {events.map((event) => (
                      <TimelineEventCard key={event.id} event={event} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TimelineEventCard({ event }: { event: TimelineEvent }) {
  const getEventIcon = () => {
    switch (event.event_type) {
      case "created":
        return <Plus className="h-3 w-3" />;
      case "removed":
        return <Minus className="h-3 w-3" />;
      case "status_changed":
        return <RefreshCw className="h-3 w-3" />;
      default:
        return <AlertTriangle className="h-3 w-3" />;
    }
  };

  const getEventColor = () => {
    switch (event.event_type) {
      case "created":
        return "bg-green-500 text-white";
      case "removed":
        return "bg-red-500 text-white";
      case "status_changed":
        return "bg-yellow-500 text-white";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getAssetIcon = () => {
    switch (event.asset_type) {
      case "subdomain":
        return <Globe className="h-4 w-4 text-blue-500" />;
      case "ip":
        return <Server className="h-4 w-4 text-green-500" />;
      case "port":
        return <Network className="h-4 w-4 text-purple-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getSeverityBadge = () => {
    switch (event.severity) {
      case "critical":
        return <Badge variant="destructive">Critical</Badge>;
      case "warning":
        return <Badge variant="warning" className="bg-yellow-500/10 text-yellow-600">Warning</Badge>;
      default:
        return null;
    }
  };

  const getEventDescription = () => {
    const details = event.details as Record<string, unknown>;

    switch (event.event_type) {
      case "created":
        return `New ${event.asset_type} discovered`;
      case "removed":
        return `${event.asset_type} removed from inventory`;
      case "status_changed":
        if (details.old_value && details.new_value) {
          return (
            <span className="flex items-center gap-1">
              Status changed: <code className="text-xs px-1 bg-muted rounded">{String(details.old_value)}</code>
              <ArrowRight className="h-3 w-3" />
              <code className="text-xs px-1 bg-muted rounded">{String(details.new_value)}</code>
            </span>
          );
        }
        return "Status changed";
      default:
        return event.event_type.replace(/_/g, " ");
    }
  };

  return (
    <Link to={`/assets/${event.asset_id}`}>
      <div className="flex gap-4 pl-2 group hover:bg-accent/50 rounded-lg p-2 transition-colors">
        {/* Event indicator */}
        <div
          className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${getEventColor()}`}
        >
          {getEventIcon()}
        </div>

        {/* Event content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              {event.asset_type === "port" ? (
                // Port assets: show cloud icon, IP and port in separate columns
                (() => {
                  const parsed = parsePortIdentifier(event.identifier);
                  const details = event.details as Record<string, unknown>;
                  const cloudInfo = getCloudProviderInfo(details.cloud_provider as string);
                  return (
                    <div className="flex items-center gap-3">
                      {/* Cloud provider icon */}
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        {cloudInfo ? (
                          <img
                            src={cloudInfo.icon}
                            alt={cloudInfo.name}
                            className="w-4 h-4"
                            title={cloudInfo.name}
                          />
                        ) : details.is_cloud ? (
                          <Cloud className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Server className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      {getAssetIcon()}
                      <span className="font-mono text-sm w-36 truncate">{parsed?.ip || event.identifier}</span>
                      <span className="font-mono text-sm font-medium">{parsed?.port || ""}</span>
                      {getSeverityBadge()}
                    </div>
                  );
                })()
              ) : event.asset_type === "ip" ? (
                // IP assets: show cloud icon
                (() => {
                  const details = event.details as Record<string, unknown>;
                  const cloudInfo = getCloudProviderInfo(details.cloud_provider as string);
                  return (
                    <div className="flex items-center gap-3">
                      {/* Cloud provider icon */}
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        {cloudInfo ? (
                          <img
                            src={cloudInfo.icon}
                            alt={cloudInfo.name}
                            className="w-4 h-4"
                            title={cloudInfo.name}
                          />
                        ) : details.is_cloud ? (
                          <Cloud className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Server className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      {getAssetIcon()}
                      <span className="font-mono text-sm truncate">{event.identifier}</span>
                      {getSeverityBadge()}
                    </div>
                  );
                })()
              ) : (
                <div className="flex items-center gap-2">
                  {getAssetIcon()}
                  <span className="font-mono text-sm truncate">{event.identifier}</span>
                  {getSeverityBadge()}
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                {getEventDescription()}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">
                {formatTimestamp(new Date(event.timestamp))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
